import { useState } from 'react';
import { Mail, MapPin, Phone, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    boatType: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const boatTypes = [
    "14'-20' Armageddon V Bottom",
    "16'-24' Pram Bow",
    "17'-20' SRJ Flat Bottom River Boat",
    "18'-32' Landing Craft",
    "14'-32' Specmar Design",
    "Custom Project",
    "Other / General Inquiry",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({
        type: 'success',
        message: 'Thank you! Your message has been sent. We\'ll get back to you soon.',
      });
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        boatType: '',
        message: '',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again or email us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Header */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="text-orange-500 font-heading text-sm uppercase tracking-widest mb-3">
            Get in Touch
          </p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Ready to start your project? Have questions? 
            We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="bg-slate-900 text-white p-8 h-full">
                <h2 className="text-2xl font-bold mb-8">Contact Information</h2>
                
                <div className="space-y-6">
                  <a 
                    href="tel:907-415-9639"
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-heading text-sm uppercase tracking-wider text-slate-400 mb-1">
                        Phone
                      </p>
                      <p className="text-lg group-hover:text-orange-500 transition-colors">
                        907-415-9639
                      </p>
                    </div>
                  </a>

                  <a 
                    href="mailto:akskiffs@gmail.com"
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-heading text-sm uppercase tracking-wider text-slate-400 mb-1">
                        Email
                      </p>
                      <p className="text-lg group-hover:text-orange-500 transition-colors">
                        akskiffs@gmail.com
                      </p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-500 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-heading text-sm uppercase tracking-wider text-slate-400 mb-1">
                        Location
                      </p>
                      <p className="text-lg">Kenny Lake, AK</p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 pt-8 border-t border-slate-700">
                  <p className="text-slate-400 leading-relaxed">
                    We're a custom boat build shop based in Alaska. 
                    Whether you need a river runner, landing craft, or something unique, 
                    we're here to build your dream boat.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white border-2 border-slate-100 p-8 md:p-12">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                  Start Your Project
                </h2>
                <p className="text-slate-500 mb-8">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>

                {status.message && (
                  <div
                    className={`p-4 mb-6 flex items-center gap-3 ${
                      status.type === 'success'
                        ? 'bg-green-50 text-green-800 border-2 border-green-200'
                        : 'bg-red-50 text-red-800 border-2 border-red-200'
                    }`}
                    data-testid="form-status"
                  >
                    {status.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    )}
                    {status.message}
                  </div>
                )}

                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block font-heading text-sm uppercase tracking-wider text-slate-500 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500 focus:bg-white transition-colors"
                        data-testid="input-firstname"
                      />
                    </div>
                    <div>
                      <label className="block font-heading text-sm uppercase tracking-wider text-slate-500 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500 focus:bg-white transition-colors"
                        data-testid="input-lastname"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block font-heading text-sm uppercase tracking-wider text-slate-500 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500 focus:bg-white transition-colors"
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <label className="block font-heading text-sm uppercase tracking-wider text-slate-500 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500 focus:bg-white transition-colors"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block font-heading text-sm uppercase tracking-wider text-slate-500 mb-2">
                      Boat Type / Interest
                    </label>
                    <select
                      name="boatType"
                      value={formData.boatType}
                      onChange={handleChange}
                      className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500 focus:bg-white transition-colors"
                      data-testid="select-boattype"
                    >
                      <option value="">Select an option</option>
                      {boatTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-8">
                    <label className="block font-heading text-sm uppercase tracking-wider text-slate-500 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell us about your project, questions, or requirements..."
                      className="w-full px-4 py-3 border-2 border-slate-200 bg-slate-50 focus:border-orange-500 focus:bg-white transition-colors resize-none"
                      data-testid="textarea-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary flex items-center justify-center gap-2 w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
